import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  message,
  Tag,
  Space,
  Form,
  Select,
  Spin,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
} from "antd";
import {
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  BookOutlined,
  EditOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  QuestionOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import withRoleAccess from "../../hoc/withRoleAccess";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";

const { Option } = Select;
const { Title, Text } = Typography;

const CoursePage = () => {
  const { groupid } = useParams();
  const API_URL = process.env.REACT_APP_API_URL;
  const [course, setCourse] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // ✨ NEW: Inline editing states
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [editedTeacherId, setEditedTeacherId] = useState(null);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editedSubjectId, setEditedSubjectId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [editedTime, setEditedTime] = useState('');
  const [editingHourId, setEditingHourId] = useState(null);
  const [editedHour, setEditedHour] = useState(null);
  // ✨ NEW: Inline editing for Platform
  const [editingPlatformId, setEditingPlatformId] = useState(null);
  const [editedPlatform, setEditedPlatform] = useState(null);


  // Format minutes to hours:minutes
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return "0:00 hr";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${hours}:${mins.toString().padStart(2, '0')} hr`;
};


  // Time options 08:00–23:00 every 15 mins
  const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
    const hour = 8 + Math.floor(index / 4);
    const minutes = (index % 4) * 15;
    const label = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    return { label, value: label };
  });

  // Duration options 15min → 3 hours (in minutes)
  const durationOptions = Array.from({ length: 12 }, (_, index) => {
    const minutes = (index + 1) * 15;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return {
      label: `${h}h ${m.toString().padStart(2, "0")}m`,
      value: minutes,
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        const [teachersRes, subjectsRes] = await Promise.all([
          axios.get(`${API_URL}/teachers`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/subjects`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setTeachers(teachersRes.data.teachers || []);
        setSubjects(subjectsRes.data || []);
      } catch (error) {
        console.error('Error fetching teachers or subjects:', error);
      }
    };
    fetchData();
  }, [API_URL]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const url = `${API_URL}/class-groups/${groupid}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCourse(res.data.course || res.data);
      setClasses(res.data.classes || []);
    } catch (err) {
      console.error("❌ [fetchCourseData] Error:", err);
      message.error("Failed to load course details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseData();
  }, [groupid]);

  // ✨ NEW: Inline edit handlers for Teacher
  const handleEditTeacherClick = (classid, teacherId) => {
    setEditingTeacherId(classid);
    setEditedTeacherId(teacherId);
  };

  const handleSaveTeacherClick = async (classid) => {
    try {
      await axios.put(
        `${API_URL}/classes/${classid}`,
        { teacher_id: editedTeacherId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Teacher updated successfully");
      setEditingTeacherId(null);
      fetchCourseData();
    } catch (error) {
      console.error("Error updating teacher:", error);
      message.error(error.response?.data?.message || "Failed to update teacher");
    }
  };

  // ✨ NEW: Inline edit handlers for Subject
  const handleEditSubjectClick = (classid, subjectId) => {
    setEditingSubjectId(classid);
    setEditedSubjectId(subjectId);
  };

  const handleSaveSubjectClick = async (classid) => {
    try {
      await axios.put(
        `${API_URL}/classes/${classid}`,
        { subjectid: editedSubjectId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Subject updated successfully");
      setEditingSubjectId(null);
      fetchCourseData();
    } catch (error) {
      console.error("Error updating subject:", error);
      message.error(error.response?.data?.message || "Failed to update subject");
    }
  };

  // ✨ NEW: Inline edit handlers for Date
  const handleEditDateClick = (classid, date) => {
    setEditingDateId(classid);
    setEditedDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleSaveDateClick = async (classid) => {
    try {
      await axios.put(
        `${API_URL}/classes/${classid}`,
        { date: editedDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Date updated successfully");
      setEditingDateId(null);
      fetchCourseData();
    } catch (error) {
      console.error("Error updating date:", error);
      message.error(error.response?.data?.message || "Failed to update date");
    }
  };

  // ✨ NEW: Inline edit handlers for Time
  const handleEditTimeClick = (classid, time) => {
    setEditingTimeId(classid);
    setEditedTime(moment(time, "HH:mm:ss").format("HH:mm"));
  };

  const handleSaveTime = async (classid) => {
    try {
      await axios.put(
        `${API_URL}/classes/${classid}`,
        { schedule_time: editedTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Time updated successfully");
      setEditingTimeId(null);
      fetchCourseData();
    } catch (error) {
      console.error("Error updating time:", error);
      message.error(error.response?.data?.message || "Failed to update time");
    }
  };

  // ✨ NEW: Inline edit handlers for Duration
  const handleEditHourClick = (classid, hour) => {
    setEditingHourId(classid);
    setEditedHour(hour);
  };

  const handleSaveHour = async (classid) => {
    try {
      await axios.put(
        `${API_URL}/classes/${classid}`,
        { schedule_hour: editedHour },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Duration updated successfully");
      setEditingHourId(null);
      fetchCourseData();
    } catch (error) {
      console.error("Error updating duration:", error);
      message.error(error.response?.data?.message || "Failed to update duration");
    }
  };

  // ✨ NEW: Start editing Platform
const handleEditPlatformClick = (classid, platform) => {
  setEditingPlatformId(classid);
  setEditedPlatform(platform);
};

// ✨ NEW: Save Platform
const handleSavePlatformClick = async (classid) => {
  try {
    await axios.put(
      `${API_URL}/classes/${classid}`,
      { platform: editedPlatform },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    message.success("Platform updated successfully");

    setEditingPlatformId(null);
    fetchCourseData();
  } catch (error) {
    console.error("Error updating platform:", error);
    message.error(error.response?.data?.message || "Failed to update platform");
  }
};


  const totalDuration = classes.reduce((sum, cls) => sum + (parseInt(cls.schedule_hour) || 0), 0);

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 180,
      render: (date, record) => {
        const isEditing = editingDateId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<CalendarOutlined />}
                onClick={() => handleEditDateClick(record.classid, record.date)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text strong>{moment(date).format("DD MMM YYYY")}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <input
              type="date"
              value={editedDate}
              onChange={(e) => setEditedDate(e.target.value)}
              style={{
                width: "100%",
                padding: "4px 11px",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                fontSize: "14px",
              }}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveDateClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingDateId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Day",
      dataIndex: "schedule_day",
      key: "schedule_day",
      width: 100,
      render: (day, record) => (
        <Tag color="blue">
          {day || moment(record.date).format("dddd")}
        </Tag>
      ),
    },
    {
      title: "Time",
      key: "time",
      width: 180,
      render: (_, record) => {
        const isEditing = editingTimeId === record.classid;

        if (!isEditing) {
          const start = moment(record.schedule_time, "HH:mm:ss").format("HH:mm");
          const end = moment(start, "HH:mm")
            .add(record.schedule_hour, "minutes")
            .format("HH:mm");

          return (
            <Space>
              <Button
                type="link"
                icon={<ClockCircleOutlined />}
                onClick={() => handleEditTimeClick(record.classid, record.schedule_time)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{start} - {end}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedTime}
              onChange={setEditedTime}
              style={{ width: "100%" }}
              options={timeOptions}
              showSearch
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveTime(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingTimeId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Hours",
      dataIndex: "schedule_hour",
      key: "schedule_hour",
      width: 150,
      render: (scheduleHour, record) => {
        const isEditing = editingHourId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditHourClick(record.classid, scheduleHour)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text style={{ color: '#555', fontWeight: 500 }}>
                {formatDuration(scheduleHour)}
              </Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedHour}
              onChange={setEditedHour}
              style={{ width: "100%" }}
              options={durationOptions}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveHour(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingHourId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Subject",
      dataIndex: "subject_name",
      key: "subject_name",
      width: 200,
      render: (text, record) => {
        const isEditing = editingSubjectId === record.classid;

        if (!isEditing) {
          return (
            <Space>
              <Button
                type="link"
                icon={<BookOutlined />}
                onClick={() => handleEditSubjectClick(record.classid, record.subject_id)}
                style={{ padding: 0, height: "auto" }}
              />
              <Text>{text || "Not assigned"}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              value={editedSubjectId}
              onChange={setEditedSubjectId}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subjects.map((subject) => (
                <Option key={subject.subjectid} value={subject.subjectid}>
                  {subject.title}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveSubjectClick(record.classid)}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingSubjectId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
  {
  title: "Teacher",
  dataIndex: "teacher_names",
  key: "teacher_names",
  width: 200,
  render: (name, record) => {
    const isEditing = editingTeacherId === record.classid;

    if (!isEditing) {
      return (
        <Space>
          <Button
            type="link"
            icon={<UserOutlined />}
            onClick={() => handleEditTeacherClick(record.classid, record.teacher_id)}
            style={{ padding: 0, height: "auto" }}
          />
          <Text>{record.nickname || name || "Not assigned"}</Text>
        </Space>
      );
    }

    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          value={editedTeacherId}
          onChange={setEditedTeacherId}
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
        >
          {teachers.map((teacher) => (
            <Option
              key={teacher.userid}
              value={teacher.userid}
              label={teacher.nickname || `${teacher.firstname} ${teacher.lastname}`}
            >
              {teacher.nickname
                ? `${teacher.nickname}`
                : `${teacher.firstname} ${teacher.lastname}`}
            </Option>
          ))}
        </Select>

        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSaveTeacherClick(record.classid)}
          >
            Save
          </Button>
          <Button size="small" onClick={() => setEditingTeacherId(null)}>
            Cancel
          </Button>
        </Space>
      </Space>
    );
  },
},

    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 160,
      render: (platform, record) => {
        const isEditing = editingPlatformId === record.classid;

        const PLATFORM_OPTIONS = [
          { value: "online", label: "Online", icon: <VideoCameraOutlined /> },
          { value: "onsite", label: "Onsite", icon: <EnvironmentOutlined /> },
          { value: "athome", label: "At Home", icon: <HomeOutlined /> },
        ];

        const getPlatformConfig = (p) => {
          switch (p) {
            case "online":
              return { text: "Online", icon: <VideoCameraOutlined />, bg: "#E6F4FF", border: "#91CAFF", color: "#0958D9" };
            case "onsite":
              return { text: "Onsite", icon: <EnvironmentOutlined />, bg: "#F6FFED", border: "#B7EB8F", color: "#389E0D" };
            case "athome":
              return { text: "At Home", icon: <HomeOutlined />, bg: "#FFF7E6", border: "#FFD591", color: "#D48806" };
            default:
              return { text: "-", icon: <QuestionOutlined />, bg: "#F0F0F0", border: "#D9D9D9", color: "#595959" };
          }
        };

        const config = getPlatformConfig(platform);

        // 🔵 VIEW MODE
        if (!isEditing) {
          return (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              lineHeight: 1,
              backgroundColor: config.bg,
              border: `1px solid ${config.border}`,
              color: config.color,
              cursor: "pointer",
            }}
            onClick={() => handleEditPlatformClick(record.classid, platform)}
            >
              {config.icon} {config.text}
            </span>
          );
        }

        // ✏️ EDIT MODE
        return (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Select
              style={{ width: "100%" }}
              value={editedPlatform}
              onChange={setEditedPlatform}
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.icon} {opt.label}
                </Option>
              ))}
            </Select>

            <Space>
              <Button type="primary" size="small" onClick={() => handleSavePlatformClick(record.classid)}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditingPlatformId(null)}>
                Cancel
              </Button>
            </Space>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f2f5' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <Header />
        <div style={{ 
          padding: "24px", 
          marginLeft: '70px', 
          marginTop: '60px',
        }}>
          <Spin spinning={loading}>
            {course && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                          <BookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                          {course.class_code}
                        </Title>
                        <Title level={4} style={{ margin: 0, color: '#666', fontWeight: 'normal' }}>
                          {course.subject_name}
                        </Title>
                      </div>
                      <Space>
                        <Tag color={course.isprivate ? "blue" : "green"} style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {course.isprivate ? "Private" : "Group"}
                        </Tag>
                        <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {course.platform}
                        </Tag>
                      </Space>
                    </div>

                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}>
                          <Statistic
                            title="Total Classes"
                            value={classes.length}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                          <Statistic
                            title="Total Hours"
                            value={formatDuration(totalDuration)}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
                          <Statistic
                            title="Period"
                            value={`${moment(course.start_date).format("DD MMM")} - ${moment(course.end_date).format("DD MMM YYYY")}`}
                            valueStyle={{ color: '#fa8c16', fontSize: '16px' }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </Space>
                </Card>

                <Card 
                      title={<Text strong style={{ fontSize: '16px' }}>Class Schedule</Text>}

                    >
                  <Table
                    columns={columns}
                    dataSource={classes}
                    rowKey="classid"
                    bordered
                    pagination={{ 
                      pageSize: 100,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`
                    }}
                  />
                </Card>
              </>
            )}
          </Spin>
        </div>
      </div>



    </div>
  );
};

export default withRoleAccess(CoursePage, ["admin", "superadmin"]);